import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatPrice, productImage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
    User, Package, ShoppingBag, TrendingUp, Plus, Edit3, Trash2, Save,
    Printer, Check, DollarSign, Ticket, BarChart3, Eye, Power, ExternalLink,
    Shield, AlertTriangle, Clock,
} from "lucide-react";

export default function VendorDashboard() {
    const { user, loading: authLoading } = useAuth();
    const { t } = useLanguage();
    const [tab, setTab] = useState("dashboard");

    if (authLoading) return <div className="p-8 text-center text-[var(--tb-muted)]">{t("Cargando…", "Loading…")}</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "vendedor" && user.role !== "admin") {
        return (
            <div className="max-w-2xl mx-auto p-8 text-center">
                <h1 className="font-display text-3xl mb-3">{t("Acceso restringido", "Access restricted")}</h1>
                <p className="text-[var(--tb-text-2)]">{t("Esta sección es solo para vendedores verificados. Si quieres solicitar acceso, contacta al administrador.", "This section is only for verified sellers. Contact the admin to request access.")}</p>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="vendor-dashboard">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="tb-overline text-[var(--tb-gold)]">{t("PANEL DE VENDEDOR", "SELLER PANEL")}</div>
                    <h1 className="font-display text-3xl md:text-4xl">{t("Tu espacio", "Your space")}</h1>
                </div>
            </div>
            <div className="flex gap-2 border-b border-[var(--tb-border)] overflow-x-auto">
                {[
                    ["dashboard", t("Resumen", "Summary"), TrendingUp],
                    ["products", t("Productos", "Products"), Package],
                    ["orders", t("Pedidos", "Orders"), ShoppingBag],
                    ["coupons", t("Cupones", "Coupons"), Ticket],
                    ["analytics", t("Analítica", "Analytics"), BarChart3],
                    ["profile", t("Perfil público", "Public profile"), User],
                ].map(([k, l, Icon]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-[var(--tb-gold)] text-[var(--tb-gold)]" : "border-transparent text-[var(--tb-text-2)] hover:text-[var(--tb-gold)]"}`}
                        data-testid={`vendor-tab-${k}`}>
                        <Icon className="w-4 h-4" /> {l}
                    </button>
                ))}
            </div>
            {tab === "dashboard" && <VendorStats />}
            {tab === "products" && <VendorProducts />}
            {tab === "orders" && <VendorOrders />}
            {tab === "coupons" && <VendorCoupons />}
            {tab === "analytics" && <VendorAnalytics />}
            {tab === "profile" && <VendorProfile />}
        </div>
    );
}

function VendorStats() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/vendor/me/stats").then(({ data }) => setS(data)).catch(() => setS({})); }, []);
    if (!s) return <div className="text-[var(--tb-muted)]">Cargando…</div>;
    const isSubscription = s.billing_model === "subscription";
    return (
        <div className="space-y-3" data-testid="vendor-stats">
            <div className="tb-card p-3 flex items-center gap-2 flex-wrap">
                <span className="tb-overline text-[var(--tb-muted)]">Modelo de cobro:</span>
                {isSubscription ? (
                    <span className="tb-badge tb-badge-gold">SUSCRIPCIÓN · {formatPrice(s.subscription_fee)}/mes</span>
                ) : (
                    <span className="tb-badge tb-badge-warm">COMISIÓN · {s.commission_percent}% por venta</span>
                )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Ingresos totales" value={formatPrice(s.total_revenue)} accent />
                {isSubscription ? (
                    <Stat label="Suscripción mensual" value={formatPrice(s.subscription_fee)} />
                ) : (
                    <Stat label={`Comisión (${s.commission_percent}%)`} value={formatPrice(s.commission_owed)} />
                )}
                <Stat label="Neto para ti" value={formatPrice(s.vendor_net)} accent />
                <Stat label="Pedidos completos" value={s.total_orders} />
                <Stat label="Productos" value={s.products_count} />
                <Stat label="Unidades vendidas" value={s.sold_units} />
            </div>
        </div>
    );
}
const Stat = ({ label, value, accent }) => (
    <div className="tb-card p-4">
        <div className="text-[10px] uppercase tracking-wider text-[var(--tb-muted)]">{label}</div>
        <div className={`text-xl font-semibold mt-1 ${accent ? "text-[var(--tb-gold)]" : ""}`}>{value}</div>
    </div>
);

function VendorProducts() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const load = () => api.get("/vendor/me/products").then(({ data }) => setItems(data));
    useEffect(() => { load(); }, []);
    const del = async (sku) => {
        if (!window.confirm("¿Eliminar producto?")) return;
        try { await api.delete(`/vendor/me/products/${sku}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    return (
        <div className="space-y-3">
            <div className="tb-card p-3 text-xs text-[var(--tb-text-2)] flex items-start gap-2" data-testid="moderation-info">
                <Shield className="w-4 h-4 text-[var(--tb-gold)] flex-shrink-0 mt-0.5" />
                <div>
                    <strong className="text-[var(--tb-text)]">Revisión antes de publicar:</strong> Cada producto nuevo (y las ediciones a nombre, precio, descripción o foto) pasa por moderación del equipo Terra antes de aparecer en el catálogo. Este control protege la calidad de la tienda.
                </div>
            </div>
            <button onClick={() => setShowNew(true)} className="tb-btn-primary inline-flex items-center gap-2" data-testid="vendor-add-product">
                <Plus className="w-4 h-4" /> Añadir producto
            </button>
            {items.length === 0 ? (
                <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">Aún no tienes productos. Crea el primero.</div>
            ) : (
                <div className="grid md:grid-cols-2 gap-3">
                    {items.map((p) => (
                        <div key={p.sku} className="tb-card p-3" data-testid={`vendor-product-${p.sku}`}>
                            <div className="flex gap-3 items-center">
                                {(() => { const s = productImage(p); return s ? <img src={s} alt={p.nombre} className="w-16 h-16 object-cover rounded" /> : <div className="w-16 h-16 bg-[var(--tb-bg-2)]" />; })()}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <div className="font-medium truncate">{p.nombre}</div>
                                        <ModerationBadge status={p.moderation_status} />
                                    </div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">{p.sku} · {p.categoria}</div>
                                    <div className="text-[var(--tb-gold)] text-sm mt-0.5">{formatPrice(p.precio_venta)} · Stock {p.cantidad_disponible}</div>
                                </div>
                                <button onClick={() => setEditing(p)} className="tb-btn-ghost !p-2" aria-label="Editar"><Edit3 className="w-4 h-4" /></button>
                                <button onClick={() => del(p.sku)} className="tb-btn-ghost !p-2 text-rose-400" aria-label="Eliminar"><Trash2 className="w-4 h-4" /></button>
                            </div>
                            {p.moderation_status === "rejected" && p.moderation_reason && (
                                <div className="mt-2 pt-2 border-t border-rose-500/30 text-xs" data-testid={`vendor-product-reason-${p.sku}`}>
                                    <div className="text-rose-400 font-medium inline-flex items-center gap-1 mb-1">
                                        <AlertTriangle className="w-3.5 h-3.5" /> Motivo del rechazo:
                                    </div>
                                    <div className="text-[var(--tb-text-2)]">{p.moderation_reason}</div>
                                    <div className="text-[10px] text-[var(--tb-muted)] mt-1">Edita el producto y se enviará automáticamente a nueva revisión.</div>
                                </div>
                            )}
                            {p.moderation_status === "pending_review" && (
                                <div className="mt-2 pt-2 border-t border-amber-500/30 text-xs text-amber-300 inline-flex items-center gap-1">
                                    <Clock className="w-3.5 h-3.5" /> En revisión por el equipo Terra. Aún no visible en el catálogo.
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {(showNew || editing) && (
                <ProductForm
                    initial={editing || {}}
                    onClose={() => { setShowNew(false); setEditing(null); }}
                    onSaved={() => { load(); setShowNew(false); setEditing(null); }}
                />
            )}
        </div>
    );
}

function ModerationBadge({ status }) {
    if (!status || status === "approved") return <span className="tb-badge tb-badge-success !text-[9px]">APROBADO</span>;
    if (status === "pending_review") return <span className="tb-badge tb-badge-warning !text-[9px]">EN REVISIÓN</span>;
    if (status === "rejected") return <span className="tb-badge !text-[9px] bg-rose-500/20 text-rose-300 border border-rose-500/40">RECHAZADO</span>;
    return null;
}

function ProductForm({ initial, onClose, onSaved }) {
    const [f, setF] = useState({
        sku: initial.sku || "",
        nombre: initial.nombre || "",
        descripcion: initial.descripcion || "",
        precio_venta: initial.precio_venta || 0,
        precio_compra: initial.precio_compra || 0,
        precio_minimo: initial.precio_minimo || 0,
        categoria: initial.categoria || "General",
        condicion: initial.condicion || "Nuevo",
        cantidad_disponible: initial.cantidad_disponible || 1,
        foto: initial.foto || "",
        foto_url: initial.foto_url || "",
        images: Array.isArray(initial.images) ? initial.images : [],
        weight_oz: initial.weight_oz ?? 8,
        length_in: initial.length_in ?? 6,
        width_in: initial.width_in ?? 4,
        height_in: initial.height_in ?? 2,
        hazmat: !!initial.hazmat,
    });
    const [saving, setSaving] = useState(false);
    const isEdit = !!initial.sku;
    const save = async () => {
        // SKU sanity check when the vendor typed one manually on create.
        const skuTrim = (f.sku || "").trim().toUpperCase();
        if (!isEdit && skuTrim && !/^[A-Z0-9\-]{2,32}$/.test(skuTrim)) {
            toast.error("SKU inválido. Usa 2-32 caracteres: letras, números y guiones. Ej: MITIENDA-001");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...f };
            if (!isEdit) payload.sku = skuTrim;  // blank => backend auto-generates V-XXXX
            else delete payload.sku;              // never resend SKU on edit
            if (isEdit) await api.patch(`/vendor/me/products/${initial.sku}`, payload);
            else await api.post("/vendor/me/products", payload);
            toast.success(isEdit ? "Actualizado" : "Creado");
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
        finally { setSaving(false); }
    };
    const uploadFoto = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error("La imagen supera 5MB"); return; }
        const r = new FileReader();
        r.onload = () => setF((cur) => ({ ...cur, foto: r.result, foto_url: "" }));
        r.readAsDataURL(file);
    };
    // Extras beyond `foto`. Capped at 5 to prevent unbounded document growth.
    const uploadExtraImages = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const remaining = 5 - (f.images?.length || 0);
        if (remaining <= 0) { toast.error("Máximo 5 imágenes extra"); return; }
        files.slice(0, remaining).forEach((file) => {
            if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: >5MB, omitida`); return; }
            const r = new FileReader();
            r.onload = () => setF((cur) => ({ ...cur, images: [...(cur.images || []), r.result] }));
            r.readAsDataURL(file);
        });
    };
    const removeExtraImage = (idx) => setF((cur) => ({ ...cur, images: cur.images.filter((_, i) => i !== idx) }));

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-3 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="tb-card p-4 max-w-2xl w-full my-4 space-y-3" data-testid="vendor-product-form">
                <h3 className="font-display text-lg">{isEdit ? "Editar producto" : "Nuevo producto"}</h3>

                <label className="text-xs block">
                    <span className="tb-overline text-[var(--tb-gold)]">ID / SKU (opcional)</span>
                    <input
                        className="tb-input font-mono mt-1"
                        placeholder="Déjalo vacío para generar uno automático"
                        value={f.sku}
                        disabled={isEdit}
                        onChange={(e) => setF({ ...f, sku: e.target.value.toUpperCase() })}
                        data-testid="vendor-prod-sku" />
                    {!isEdit && (
                        <p className="text-[10px] text-[var(--tb-muted)] mt-1">2-32 caracteres · letras, números y guiones · MAYÚSCULAS.</p>
                    )}
                </label>

                <input className="tb-input" placeholder="Nombre *" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} data-testid="vendor-prod-nombre" />
                <textarea className="tb-input" placeholder="Descripción" rows={3} value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} data-testid="vendor-prod-desc" />

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <label className="text-xs">Precio compra (USD)
                        <input type="number" step="0.01" min="0" className="tb-input mt-1" value={f.precio_compra} onChange={(e) => setF({ ...f, precio_compra: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-p-compra" />
                    </label>
                    <label className="text-xs">Precio venta (USD)
                        <input type="number" step="0.01" min="0" className="tb-input mt-1" value={f.precio_venta} onChange={(e) => setF({ ...f, precio_venta: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-p-venta" />
                    </label>
                    <label className="text-xs">Precio mínimo (USD)
                        <input type="number" step="0.01" min="0" className="tb-input mt-1" value={f.precio_minimo} onChange={(e) => setF({ ...f, precio_minimo: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-p-minimo" />
                    </label>
                    <label className="text-xs">Cantidad
                        <input type="number" min="0" className="tb-input mt-1" value={f.cantidad_disponible} onChange={(e) => setF({ ...f, cantidad_disponible: parseInt(e.target.value || 0) })} data-testid="vendor-prod-qty" />
                    </label>
                    <label className="text-xs">Categoría
                        <input className="tb-input mt-1" value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} data-testid="vendor-prod-cat" />
                    </label>
                    <label className="text-xs">Condición
                        <select className="tb-input mt-1" value={f.condicion} onChange={(e) => setF({ ...f, condicion: e.target.value })} data-testid="vendor-prod-cond">
                            <option>Nuevo</option><option>Usado</option>
                        </select>
                    </label>
                </div>

                <div>
                    <div className="tb-overline text-[var(--tb-gold)] mb-1">Dimensiones para envío</div>
                    <div className="grid grid-cols-4 gap-2">
                        <label className="text-xs">Peso (oz)
                            <input type="number" step="0.1" min="0" className="tb-input mt-1" value={f.weight_oz} onChange={(e) => setF({ ...f, weight_oz: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-weight" />
                        </label>
                        <label className="text-xs">Largo (in)
                            <input type="number" step="0.1" min="0" className="tb-input mt-1" value={f.length_in} onChange={(e) => setF({ ...f, length_in: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-length" />
                        </label>
                        <label className="text-xs">Ancho (in)
                            <input type="number" step="0.1" min="0" className="tb-input mt-1" value={f.width_in} onChange={(e) => setF({ ...f, width_in: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-width" />
                        </label>
                        <label className="text-xs">Alto (in)
                            <input type="number" step="0.1" min="0" className="tb-input mt-1" value={f.height_in} onChange={(e) => setF({ ...f, height_in: parseFloat(e.target.value || 0) })} data-testid="vendor-prod-height" />
                        </label>
                    </div>
                </div>

                <label className="text-xs inline-flex items-center gap-2">
                    <input type="checkbox" checked={!!f.hazmat} onChange={(e) => setF({ ...f, hazmat: e.target.checked })} data-testid="vendor-prod-hazmat" />
                    Producto peligroso / Hazmat (baterías, líquidos, aerosoles…)
                </label>

                <label className="text-xs block">Foto principal
                    <input type="file" accept="image/*" onChange={uploadFoto} className="tb-input mt-1" data-testid="vendor-prod-foto" />
                </label>
                {(() => { const s = productImage(f); return s ? <img src={s} alt="preview" className="w-24 h-24 object-cover rounded" /> : null; })()}

                <label className="text-xs block">Imágenes extra (máx 5)
                    <input type="file" accept="image/*" multiple onChange={uploadExtraImages} className="tb-input mt-1" data-testid="vendor-prod-extra-images" />
                </label>
                {(f.images || []).length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {f.images.map((src, i) => (
                            <div key={i} className="relative">
                                <img src={src} alt={`extra-${i}`} className="w-16 h-16 object-cover rounded" />
                                <button type="button" onClick={() => removeExtraImage(i)}
                                    className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full w-5 h-5 text-[10px] leading-none"
                                    data-testid={`vendor-prod-remove-image-${i}`}>×</button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="tb-btn-secondary !text-xs" data-testid="vendor-prod-cancel">Cancelar</button>
                    <button onClick={save} disabled={saving} className="tb-btn-primary !text-xs inline-flex items-center gap-1" data-testid="vendor-prod-save"><Save className="w-4 h-4" /> Guardar</button>
                </div>
            </div>
        </div>
    );
}

function VendorOrders() {
    const [orders, setOrders] = useState([]);
    const load = () => api.get("/vendor/me/orders").then(({ data }) => setOrders(data));
    useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);
    const confirmPay = async (id) => {
        try { await api.put(`/vendor/me/orders/${id}/confirm-payment`); toast.success("Pago confirmado"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const createGuide = async (id) => {
        try { const { data } = await api.post(`/vendor/me/orders/${id}/create-guide`, {}); toast.success(`Guía ${data.guia_numero} creada — cliente notificado`); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    if (!orders.length) return <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">Aún no tienes pedidos.</div>;
    return (
        <div className="space-y-3">
            {orders.map((o) => (
                <div key={o._id} className="tb-card p-4" data-testid={`vendor-order-${o._id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                        <div>
                            <div className="text-[10px] text-[var(--tb-muted)] font-mono">{o._id.slice(0, 12)}</div>
                            <div className="font-medium">{o.customer_name}</div>
                            <div className="text-xs text-[var(--tb-muted)]">{o.user_email} · {o.customer_phone}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-[var(--tb-gold)] text-lg">{formatPrice(o.my_subtotal)}</div>
                            <div className="text-[10px] text-[var(--tb-muted)]">
                                Comisión {formatPrice(o.commission_amount)} · Neto <span className="text-[#4CAF50]">{formatPrice(o.vendor_net)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="text-xs space-y-0.5">
                        {o.my_items.map((i, k) => <div key={k}>{i.nombre} ×{i.quantity} · {formatPrice(i.subtotal)}</div>)}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[var(--tb-border)]">
                        <span className="tb-badge tb-badge-muted">{o.payment_method?.toUpperCase()}</span>
                        {o.payment_confirmed ? <span className="tb-badge tb-badge-success"><Check className="w-3 h-3 inline" /> Pagado</span>
                            : <button onClick={() => confirmPay(o._id)} className="tb-btn-primary !text-xs inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Confirmar pago</button>}
                        {o.c807_guia ? (
                            <span className="tb-badge tb-badge-success">Guía: {o.c807_guia}</span>
                        ) : (o.payment_confirmed && o.needs_shipping) && (
                            <button onClick={() => createGuide(o._id)} className="tb-btn-secondary !text-xs !text-emerald-300 !border-emerald-500/50 inline-flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> Crear guía C807</button>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

function VendorCoupons() {
    const [items, setItems] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const [editing, setEditing] = useState(null);
    const load = () => api.get("/vendor/me/coupons").then(({ data }) => setItems(data)).catch(() => setItems([]));
    useEffect(() => { load(); }, []);
    const del = async (code) => {
        if (!window.confirm(`¿Eliminar el cupón ${code}?`)) return;
        try { await api.delete(`/vendor/me/coupons/${code}`); toast.success("Eliminado"); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    const toggle = async (code) => {
        try { await api.post(`/vendor/me/coupons/${code}/toggle`); load(); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
    };
    return (
        <div className="space-y-3" data-testid="vendor-coupons">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-[var(--tb-text-2)] max-w-xl">Los cupones que crees aquí solo aplican a los productos de <strong>tu boutique</strong>. Compártelos con tus clientes para incentivar sus compras.</p>
                <button onClick={() => setShowNew(true)} className="tb-btn-primary inline-flex items-center gap-2 !text-xs" data-testid="vendor-add-coupon">
                    <Plus className="w-4 h-4" /> Nuevo cupón
                </button>
            </div>
            {items.length === 0 ? (
                <div className="tb-card p-8 text-center text-[var(--tb-text-2)]">Aún no tienes cupones. Crea el primero para tus clientes.</div>
            ) : (
                <div className="space-y-2">
                    {items.map((c) => (
                        <div key={c.code} className="tb-card p-3 flex items-center gap-3 flex-wrap" data-testid={`vendor-coupon-${c.code}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-sm text-[var(--tb-gold)]">{c.code}</span>
                                    {c.active ? <span className="tb-badge tb-badge-success !text-[9px]">ACTIVO</span> : <span className="tb-badge tb-badge-muted !text-[9px]">PAUSADO</span>}
                                </div>
                                <div className="text-[10px] text-[var(--tb-muted)] truncate">{c.name || "Sin descripción"}</div>
                                <div className="text-[10px] text-[var(--tb-muted)] mt-0.5">
                                    {c.type === "percent" ? `${c.amount}% de descuento` : `${formatPrice(c.amount)} de descuento`}
                                    {c.min_subtotal > 0 && ` · min ${formatPrice(c.min_subtotal)}`}
                                    {c.max_uses ? ` · ${c.uses_count}/${c.max_uses} usos` : ` · ${c.uses_count} usos`}
                                </div>
                            </div>
                            <button onClick={() => toggle(c.code)} className="tb-btn-ghost !p-1.5" title="Activar/Pausar"><Power className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setEditing(c)} className="tb-btn-ghost !p-1.5" title="Editar"><Edit3 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => del(c.code)} className="tb-btn-ghost !p-1.5 text-rose-400" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                    ))}
                </div>
            )}
            {(showNew || editing) && (
                <CouponForm
                    initial={editing || {}}
                    onClose={() => { setShowNew(false); setEditing(null); }}
                    onSaved={() => { load(); setShowNew(false); setEditing(null); }}
                />
            )}
        </div>
    );
}

function CouponForm({ initial, onClose, onSaved }) {
    const isEdit = !!initial.code;
    const [f, setF] = useState({
        code: initial.code || "",
        name: initial.name || "",
        type: initial.type || "percent",
        amount: initial.amount ?? 10,
        min_subtotal: initial.min_subtotal ?? 0,
        max_uses: initial.max_uses ?? "",
        active: initial.active ?? true,
    });
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                code: (f.code || "").trim().toUpperCase(),
                name: f.name || "",
                type: f.type,
                amount: parseFloat(f.amount || 0),
                min_subtotal: parseFloat(f.min_subtotal || 0),
                max_uses: f.max_uses ? parseInt(f.max_uses, 10) : null,
                active: !!f.active,
            };
            if (isEdit) await api.patch(`/vendor/me/coupons/${initial.code}`, payload);
            else await api.post("/vendor/me/coupons", payload);
            toast.success(isEdit ? "Cupón actualizado" : "Cupón creado");
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
        finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-3 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="tb-card p-5 max-w-md w-full my-4 space-y-3" data-testid="vendor-coupon-form">
                <h3 className="font-display text-lg">{isEdit ? "Editar cupón" : "Nuevo cupón"}</h3>
                <label className="text-xs block">Código *
                    <input className="tb-input mt-1 uppercase" placeholder="EJ: JULIO10" value={f.code}
                        disabled={isEdit}
                        onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, "") })} />
                </label>
                <label className="text-xs block">Descripción (opcional)
                    <input className="tb-input mt-1" placeholder="Descuento de bienvenida" value={f.name}
                        onChange={(e) => setF({ ...f, name: e.target.value })} />
                </label>
                <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs">Tipo
                        <select className="tb-input mt-1" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                            <option value="percent">Porcentaje (%)</option>
                            <option value="fixed">Monto fijo (USD)</option>
                        </select>
                    </label>
                    <label className="text-xs">Valor {f.type === "percent" ? "(%)" : "(USD)"}
                        <input type="number" step={f.type === "percent" ? "1" : "0.01"} min="0.01"
                            max={f.type === "percent" ? "90" : undefined}
                            className="tb-input mt-1" value={f.amount}
                            onChange={(e) => setF({ ...f, amount: e.target.value })} />
                    </label>
                    <label className="text-xs">Compra mínima (USD)
                        <input type="number" step="0.01" min="0" className="tb-input mt-1" value={f.min_subtotal}
                            onChange={(e) => setF({ ...f, min_subtotal: e.target.value })} />
                    </label>
                    <label className="text-xs">Usos máximos (vacío = ilimitado)
                        <input type="number" min="1" className="tb-input mt-1" value={f.max_uses}
                            onChange={(e) => setF({ ...f, max_uses: e.target.value })} />
                    </label>
                </div>
                <label className="inline-flex items-center gap-2 text-xs">
                    <input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
                    Activo
                </label>
                <div className="text-[10px] text-[var(--tb-muted)]">Este cupón solo aplicará al subtotal de tus productos en el carrito, no a productos de otros vendedores ni de la tienda principal.</div>
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="tb-btn-secondary !text-xs">Cancelar</button>
                    <button onClick={save} disabled={saving} className="tb-btn-primary !text-xs inline-flex items-center gap-1" data-testid="vendor-coupon-save">
                        <Save className="w-4 h-4" /> Guardar
                    </button>
                </div>
            </div>
        </div>
    );
}

function VendorAnalytics() {
    const [a, setA] = useState(null);
    const { user } = useAuth();
    useEffect(() => { api.get("/vendor/me/analytics").then(({ data }) => setA(data)).catch(() => setA({ top_products: [] })); }, []);
    if (!a) return <div className="text-[var(--tb-muted)]">Cargando…</div>;
    const publicUrl = `/v/${user?.id}`;
    return (
        <div className="space-y-4" data-testid="vendor-analytics">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Stat label="Visitas al perfil" value={a.profile_views ?? 0} accent />
                <Stat label="Vistas de productos" value={a.products_views_total ?? 0} />
                <Stat label="Unidades vendidas" value={a.products_sold_total ?? 0} />
            </div>
            <Link to={publicUrl} className="inline-flex items-center gap-1 text-xs text-[var(--tb-gold)] hover:underline">
                <ExternalLink className="w-3.5 h-3.5" /> Ver mi perfil público
            </Link>
            <div>
                <h3 className="font-medium mb-2 text-sm">Top productos por vistas</h3>
                {(!a.top_products || a.top_products.length === 0) ? (
                    <div className="tb-card p-6 text-center text-[var(--tb-text-2)] text-xs">Aún no hay vistas registradas. Comparte tu perfil en redes para atraer tráfico.</div>
                ) : (
                    <div className="space-y-1">
                        {a.top_products.map((p) => (
                            <div key={p.sku} className="tb-card p-2 flex items-center gap-3" data-testid={`vendor-analytics-top-${p.sku}`}>
                                {(() => { const s = productImage(p); return s ? <img src={s} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 bg-[var(--tb-bg-2)] rounded" />; })()}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm truncate">{p.nombre}</div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">{p.sku}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs inline-flex items-center gap-1 text-[var(--tb-gold)]"><Eye className="w-3 h-3" />{p.views}</div>
                                    <div className="text-[10px] text-[var(--tb-muted)]">🔥 {p.sold_count}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function VendorProfile() {
    const [me, setMe] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get("/vendor/me").then(({ data }) => setMe(data)); }, []);
    if (!me) return <div className="text-[var(--tb-muted)]">Cargando…</div>;
    const upd = (k, v) => setMe({ ...me, [k]: v });
    const uploadFoto = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const r = new FileReader();
        r.onload = () => upd("foto", r.result);
        r.readAsDataURL(file);
    };
    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/vendor/me/profile", {
                display_name: me.display_name,
                foto: me.foto,
                bio: me.bio,
                categoria: me.categoria,
                social_links: me.social_links || {},
            });
            toast.success("Perfil actualizado");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
        finally { setSaving(false); }
    };
    const billingLabel = me.billing_model === "subscription"
        ? `Suscripción ${formatPrice(me.subscription_fee)}/mes`
        : `Comisión ${me.commission_percent}% por venta`;
    return (
        <div className="tb-card p-5 space-y-3 max-w-lg" data-testid="vendor-profile-form">
            <div className="flex items-center gap-4">
                {me.foto ? <img src={me.foto} alt="" className="w-20 h-20 rounded-full object-cover" /> : <div className="w-20 h-20 rounded-full bg-[var(--tb-bg-2)]" />}
                <div>
                    <div className="font-display text-lg">{me.display_name}</div>
                    <div className="text-[10px] text-[var(--tb-muted)]">{billingLabel}</div>
                </div>
            </div>
            <label className="text-xs block">Nombre público
                <input className="tb-input mt-1" value={me.display_name || ""} onChange={(e) => upd("display_name", e.target.value)} />
            </label>
            <label className="text-xs block">Categoría
                <input className="tb-input mt-1" placeholder="Ej. Ropa, Perfumería, Accesorios" value={me.categoria || ""} onChange={(e) => upd("categoria", e.target.value)} />
            </label>
            <label className="text-xs block">Bio
                <textarea rows={3} className="tb-input mt-1" value={me.bio || ""} onChange={(e) => upd("bio", e.target.value)} />
            </label>
            <label className="text-xs block">Foto de perfil
                <input type="file" accept="image/*" onChange={uploadFoto} className="tb-input mt-1" />
            </label>
            <div className="grid grid-cols-2 gap-2">
                {["instagram", "facebook", "tiktok", "whatsapp"].map((net) => (
                    <label key={net} className="text-xs block">{net}
                        <input className="tb-input mt-1" value={(me.social_links || {})[net] || ""}
                            onChange={(e) => upd("social_links", { ...(me.social_links || {}), [net]: e.target.value })} />
                    </label>
                ))}
            </div>
            <button onClick={save} disabled={saving} className="tb-btn-primary inline-flex items-center gap-2"><Save className="w-4 h-4" /> Guardar</button>
        </div>
    );
}
